import { ApiError, apiGet } from './client';
import type { UserRow } from '../types';

export type AiStatus = {
  ok: boolean;
  reason: 'ok' | 'no_key' | 'credit_balance' | 'auth' | 'error';
  detail: string;
  checkedAt: number;
};

async function send<T = unknown>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data: T | { error?: string };
  try {
    data = await res.json();
  } catch {
    data = {} as T;
  }
  if (!res.ok) {
    const detail = (data as { error?: string }).error ?? `${res.status} ${res.statusText}`;
    throw new ApiError(detail, res.status);
  }
  return data as T;
}

export const adminApi = {
  listUsers: () => apiGet<UserRow[]>('/api/admin/users'),
  getAiStatus: () => apiGet<AiStatus>('/api/admin/ai-status'),
  createUser: (input: {
    email: string;
    name?: string;
    role?: UserRow['role'];
    afNumbers?: string[];
  }) => send<{ success: boolean; id: number }>('/api/admin/users', 'POST', input),
  updateUser: (
    id: number,
    input: { name?: string; role?: UserRow['role']; active?: boolean; email?: string },
  ) => send<{ success: boolean }>(`/api/admin/users/${id}`, 'PUT', input),
  deleteUser: (id: number) =>
    send<{ success: boolean }>(`/api/admin/users/${id}`, 'DELETE'),
  sendInvite: (input: {
    email: string;
    name?: string;
    role?: UserRow['role'];
    afNumber?: string;
  }) => send<{ success: boolean }>('/api/admin/users/send-invite', 'POST', input),
  resendSetup: (id: number) =>
    send<{ success: boolean }>(`/api/admin/users/${id}/resend`, 'POST'),
  setAfAccess: (id: number, afNumbers: string[]) =>
    send<{ success: boolean }>(`/api/admin/users/${id}/af-access`, 'PUT', { afNumbers }),
  syncSheet: () =>
    send<{ synced?: number; created?: number; usersCreated?: number }>(
      '/api/admin/sync-sheet',
      'POST',
    ),
  impersonate: (userId: number) =>
    send<{ user: { id: number; email: string; role: string } }>(
      `/api/admin/impersonate/${userId}`,
      'POST',
    ),
};
